import { KdsLink, KdsText } from "@kazaar-platform/design-system";
import { T, useTranslate } from "@tolgee/react";
import { type ReactNode, useMemo } from "react";
import { OrderItemDetailsOrderLogReadDtoActionTypeEnum } from "@/config/apiClient";
import { type TransitionData } from "@/types/transitionData";
import { OrderItemPageActivityLogCancellableStep } from "../OrderItemPageActivityLogCancellableStep";
import { type OrderItemPageActivityLogProofProps } from "./OrderItemPageActivityLogProof.types";

export const OrderItemPageActivityLogProof = ({
  associatedOrderItemsLength,
  file,
  refusalReason,
  orderItemLog,
}: OrderItemPageActivityLogProofProps) => {
  const { t } = useTranslate();

  const transitionData = orderItemLog.transitionData as TransitionData;

  // Memoize order item data
  const orderItemData = useMemo(
    () => ({
      actionType: orderItemLog.actionType,
      transitionData: transitionData,
      relatedOrderItemsIds: transitionData?.relatedOrderItemsIds,
      orderItemName: transitionData?.orderItemName,
    }),
    [orderItemLog.actionType, transitionData]
  );

  // Memoize custom message calculation
  const customMessage = useMemo((): ReactNode => {
    switch (orderItemData.actionType) {
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelledPreviouslyRefused:
        return (
          <div className="flex items-center gap-sp-xs2">
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.proofs.cancelledPreviouslyRefused"
              )}
            </KdsText>
            {file && (
              <KdsLink href={file.url} target="_blank" size="sm">
                {file.name}
              </KdsLink>
            )}
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.proofs.hasBeenSuspended"
              )}
            </KdsText>
          </div>
        );
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelledPreviouslyValidated: {
        return (
          <div className="flex items-center gap-sp-xs2">
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.proofs.cancelledPreviouslyValidated"
              )}
            </KdsText>
            {file && (
              <KdsLink href={file.url} target="_blank" size="sm">
                {file.name}
              </KdsLink>
            )}
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.proofs.hasBeenSuspended"
              )}
            </KdsText>
          </div>
        );
      }
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelled:
        return (
          <div className="flex items-baseline gap-sp-xs2">
            <T
              keyName="order.myOrdersItemDetails.activityFeed.action.proofs.cancelledWorkflow"
              params={{
                productName: orderItemData.orderItemName,
                b: <KdsText size="subtitle-sm" />,
              }}
            />
          </div>
        );
      default:
        return null;
    }
  }, [orderItemData.actionType, orderItemData.orderItemName, file, t]);

  // Memoize info icon label
  const infoIconLabel = useMemo(() => {
    switch (orderItemData.actionType) {
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RefusedProof:
        return t(
          "order.myOrdersItemDetails.activityFeed.action.infoIcon.refused"
        );
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ConfirmProof:
        return t(
          "order.myOrdersItemDetails.activityFeed.action.infoIcon.validated"
        );
      case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddProof:
        return t(
          "order.myOrdersItemDetails.activityFeed.action.infoIcon.proposed",
          { count: associatedOrderItemsLength }
        );
      default:
        return t(
          "order.myOrdersItemDetails.activityFeed.action.infoIcon.proposed",
          { count: associatedOrderItemsLength }
        );
    }
  }, [orderItemData.actionType, t]);

  return (
    <OrderItemPageActivityLogCancellableStep
      orderItemLog={orderItemLog}
      associatedOrderItemsLength={associatedOrderItemsLength}
      refusalReason={refusalReason}
      customMessage={customMessage}
      introText={t(
        "order.myOrdersItemDetails.activityFeed.action.proofs.intro"
      )}
      suffixText={t(
        "order.myOrdersItemDetails.activityFeed.action.proofs.suffix"
      )}
      file={file}
      infoIconLabel={infoIconLabel}
    />
  );
};
